import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Select,
    IconButton,
    Typography,
    Autocomplete,
    InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import {
    Activity,
    ActivityCategory,
    Technology,
    Employee,
    CreateActivityDto,
    UpdateActivityDto,
} from '../../types';
import AiGenerator from './AiGenerator';

interface ActivityModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (activity: CreateActivityDto | UpdateActivityDto) => Promise<void>;
    activity?: Activity | null;
    activities: Activity[];
    categories: ActivityCategory[];
    technologies: Technology[];
    employees: Employee[];
    headingOverride?: string;
}

const ActivityModal: React.FC<ActivityModalProps> = ({
    open,
    onClose,
    onSave,
    activity,
    activities,
    categories,
    technologies,
    employees,
    headingOverride,
}) => {
    const [formData, setFormData] = useState({
        id: 0,
        categoryId: 0,
        technologyId: 0,
        name: '',
        description: '',
        pointOfContactId: null as number | null,
        status: '',
    });

    const [categoryInput, setCategoryInput] = useState('');
    const [technologyInput, setTechnologyInput] = useState('');
    // Remove descriptionInput, use formData.description as single source of truth
    const [pointOfContactInput, setPointOfContactInput] = useState('');
    const [aiInput, setAiInput] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submitLockRef = React.useRef(false);
    const [isExistingActivity, setIsExistingActivity] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });
    // Timer ref for auto-hide
    const snackbarTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const isAddMode = !activity;

    const [showAiGenerator, setShowAiGenerator] = useState(false);

    useEffect(() => {
        if (activity) {
            setFormData({
                id: activity.id,
                categoryId: activity.categoryId,
                technologyId: activity.technologyId,
                name: activity.name,
                description: activity.description,
                pointOfContactId: activity.pointOfContactId,
                status: activity.status,
            });
            setCategoryInput(activity.categoryName ?? '');
            setTechnologyInput(activity.technologyName ?? '');
            setPointOfContactInput(
                employees.find(e => e.id === activity.pointOfContactId)?.displayName || ''
            );
        } else {
            setFormData({
                id: 0,
                categoryId: 0,
                technologyId: 0,
                name: '',
                description: '',
                pointOfContactId: null,
                status: '',
            });
            setCategoryInput('');
            setTechnologyInput('');
            setPointOfContactInput('');
            setAiInput('');
        }
        setErrors({});
        setIsExistingActivity(false);
        setSnackbar({ open: false, message: '' }); // Reset snackbar on open/close
        // setShowAiGenerator(false); // Reset AI Generator open state on modal open/close
    }, [activity, open, employees]);

    const handleChange = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const sanitizeNameInput = (value: string) =>
        value.replace(/^\s+/, '').replace(/\s{2,}/g, ' ');

    // Normalization function (matches parent page logic)
    const normalizeActivityName = (name: string) =>
        (name || "")
            .toLowerCase()
            .replace(/[^a-z0-9]/gi, "") // remove all non-alphanumeric
            .replace(/\s+/g, ""); // remove all spaces

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!formData.categoryId) newErrors.categoryId = 'Category is required';
        if (!formData.technologyId) newErrors.technologyId = 'Technology is required';
        // Name validation
        const trimmedName = formData.name.trim();
        if (!trimmedName) {
            newErrors.name = 'Activity required';
        } else if (formData.name.length > 80) {
            newErrors.name = 'Please limit to 80 characters';
        } else if (!/[a-zA-Z]/.test(formData.name)) {
            newErrors.name = 'Activity name cannot be only numbers or special characters';
        } else {
            const inputNorm = normalizeActivityName(formData.name);
            const duplicate = activities.some(
                a =>
                    normalizeActivityName(a.name) === inputNorm &&
                    String(a.id) !== String(activity?.id)
            );
            if (duplicate) {
                newErrors.name = 'Activity name already exists';
                setSnackbar({ open: true, message: 'An activity with this name already exists.' });
                // Auto-hide after 5 seconds
                if (snackbarTimer.current) clearTimeout(snackbarTimer.current);
                snackbarTimer.current = setTimeout(() => {
                    setSnackbar({ open: false, message: '' });
                }, 5000);
            }
        }
        if (!formData.description.trim()) newErrors.description = 'Description is required';
        if (!formData.pointOfContactId) newErrors.pointOfContactId = 'Point of Contact is required';
        if (!formData.status) newErrors.status = 'Status is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0 && !snackbar.open;
    };

    const handleSubmit = async () => {
        if (submitLockRef.current || isSubmitting) return;
        if (!validate()) return;
        try {
            submitLockRef.current = true;
            setIsSubmitting(true);
            const payload = {
                ...formData,
                categoryId: formData.categoryId ?? 0,
                technologyId: formData.technologyId ?? 0,
                pointOfContactId: formData.pointOfContactId ?? 0,
            };
            if (activity) {
                await onSave({ ...payload, id: activity.id } as UpdateActivityDto);
            } else {
                const { id, ...createPayload } = payload;
                await onSave(createPayload as CreateActivityDto);
            }
            onClose();
        } finally {
            setIsSubmitting(false);
            submitLockRef.current = false;
        }
    };

    // Handler for AI autofill
    const handleAiGenerate = (result: { name: string; description: string }) => {
        setFormData(prev => ({ ...prev, name: result.name, description: result.description }));
        setShowAiGenerator(false);
    };

    // ── Dark mode detection ──
    const isDark = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

    // ── Styles ──
    const labelStyle: React.CSSProperties = {
        fontSize: '12px',
        fontWeight: 500,
        color: isDark ? '#fff' : '#1a1a1a',
        marginBottom: '4px',
        display: 'block',
    };
    const requiredStar: React.CSSProperties = { color: '#d32f2f', marginLeft: '2px' };
    const fieldWrap: React.CSSProperties = { marginBottom: '10px' };
    // Inline error style for dark mode (only valid CSSProperties)
    const errorTextStyle: React.CSSProperties = isDark ? { color: '#d32f2f' } : {};

    // Input style
    // In dark mode, use a less prominent color for autofilled, non-editable fields (except Activity Name)
    const inputStyle = {
        borderRadius: '6px',
        backgroundColor: isDark ? '#1e293b' : '#fff',
        color: isDark ? '#f1f5f9' : undefined,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? 'rgba(255,255,255,0.25)' : '#d0d5dd' },
        '& .MuiInputBase-root': { fontSize: '12px', color: isDark ? '#f1f5f9' : undefined },
        '& .MuiInputBase-input': { padding: '6px 8px', color: isDark ? '#f1f5f9' : undefined, WebkitTextFillColor: isDark ? '#f1f5f9' : undefined },
        '& textarea': { color: isDark ? '#f1f5f9' : undefined, WebkitTextFillColor: isDark ? '#f1f5f9' : undefined },
        // For disabled fields in dark mode, use a less prominent color
        ...(isDark && {
            '& .Mui-disabled': {
                color: '#94a3b8',
                WebkitTextFillColor: '#94a3b8',
                opacity: 1,
            },
        }),
    };

    // Dropdown menu styles for dark mode
    const dropdownMenuSx = {
        backgroundColor: isDark ? '#1e293b' : '#fff',
        color: isDark ? '#f1f5f9' : '#1e293b',
    };

    // Status select style
    const statusStyle = {
        borderRadius: '6px',
        backgroundColor: isDark ? '#1e293b' : '#fff',
        fontSize: '12px',
        height: '34px',
        color: isDark ? '#f1f5f9' : undefined,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? 'rgba(255,255,255,0.25)' : '#d0d5dd' },
        '& .MuiSelect-select': {
            padding: '6px 8px !important',
            fontSize: '12px',
            lineHeight: '1.4',
            display: 'flex',
            alignItems: 'center',
        },
        ...(isDark && {
            '& .MuiSelect-icon': { color: '#cbd5f5' },
        }),
    };

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                onClose();
            }}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    padding: '0',
                    maxHeight: '90vh',
                    backgroundColor: isDark ? '#1e293b' : '#fff',
                },
            }}
        >
            {/* ── Header ── */}
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: isDark ? '1px solid #334155' : '1px solid #e0e0e0',
                    py: 1.5,
                    px: 3,
                    backgroundColor: isDark ? '#1e293b' : '#fff',
                }}
            >
                <Typography fontWeight={700} fontSize={16} sx={{ color: isDark ? '#fff' : '#124B84' }}>
                    {headingOverride || (activity ? 'Edit Activity' : 'Add Activity')}
                </Typography>
                <IconButton onClick={onClose} size="small" sx={{ color: isDark ? '#38bdf8' : '#124B84' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            {/* ── Form ── */}
            <DialogContent sx={{ px: 3, pt: 2, pb: 0, backgroundColor: isDark ? '#1e293b' : '#fff' }}>

                                {/* 1. Name with AI Generator */}
                                <div style={fieldWrap}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                                        <label style={labelStyle}>
                                            Name<span style={requiredStar}>*</span>
                                        </label>
                                        {!showAiGenerator && (
                                            <Button
                                                variant="text"
                                                size="small"
                                                startIcon={<AutoAwesomeIcon sx={{ color: '#a259f7', fontSize: 18 }} />}
                                                sx={{
                                                    color: '#a259f7',
                                                    fontWeight: 500,
                                                    textTransform: 'none',
                                                    fontSize: 13,
                                                    minWidth: 0,
                                                    p: 0,
                                                    ml: 2,
                                                    lineHeight: 1,
                                                    background: 'none',
                                                    boxShadow: 'none',
                                                    borderRadius: '6px',
                                                    transition: 'background 0.2s',
                                                    '&:hover': {
                                                        background: 'rgba(162,89,247,0.08)',
                                                        color: '#7c2ae8',
                                                        textDecoration: 'none',
                                                    },
                                                }}
                                                onClick={() => setShowAiGenerator(true)}
                                            >
                                                Create using AI
                                            </Button>
                                        )}
                                    </div>
                                    {showAiGenerator && (
                                        <AiGenerator
                                            onGenerate={handleAiGenerate}
                                            onClose={() => setShowAiGenerator(false)}
                                            category={categories.find(c => c.id === formData.categoryId)?.name}
                                            technology={technologies.find(t => t.id === formData.technologyId)?.name}
                                        />
                                    )}
                    <Autocomplete
                        freeSolo
                        options={Array.from(
                            new Map((activities || []).map((a: any) => [a.id ?? a.name, a])).values()
                        )
                            .slice()
                            .sort((x: any, y: any) =>
                                String(x.name || x.activityName || '').toLowerCase().localeCompare(
                                    String(y.name || y.activityName || '').toLowerCase()
                                )
                            )}
                        getOptionLabel={(option: any) =>
                            typeof option === 'string' ? option : option?.name ?? option?.activityName ?? ''
                        }
                        value={
                            isExistingActivity
                                ? (activities || []).find(a => a.id === formData.id) || null
                                : formData.name
                                    ? { name: formData.name }
                                    : null
                        }
                        onChange={(_, value) => {
                            if (!value) {

    if (isExistingActivity && isAddMode) {

        setIsExistingActivity(false);

        // Clear autofilled data only for existing-activity selection in add mode
        setFormData({
            id: 0,
            categoryId: 0,
            technologyId: 0,
            name: '',
            description: '',
            pointOfContactId: null,
            status: '',
        });

        setCategoryInput('');
        setTechnologyInput('');
        setPointOfContactInput('');

    } else {

        // Only clear name
        setIsExistingActivity(false);

        handleChange('name', '');
        handleChange('id', 0);
    }

    setErrors({});
    return;
}
                            if (typeof value === 'string') {
                                setIsExistingActivity(false);
                                handleChange('name', sanitizeNameInput(value));
                                handleChange('id', 0);
                                setErrors({});
                                return;
                            }
                            const selected = value as any;
                            setIsExistingActivity(true);
                            setFormData({
                                id: selected.id ?? selected.activityId ?? 0,
                                name: selected.name ?? selected.activityName ?? '',
                                categoryId: selected.categoryId ?? 0,
                                technologyId: selected.technologyId ?? 0,
                                description: selected.description ?? selected.activityDescription ?? '',
                                pointOfContactId: selected.pointOfContactId ?? null,
                                status: selected.status ?? '',
                            });
                            setCategoryInput(
                                categories.find(c => c.id === (selected.categoryId ?? 0))?.name || ''
                            );
                            setTechnologyInput(
                                technologies.find(t => t.id === (selected.technologyId ?? 0))?.name || ''
                            );
                            setPointOfContactInput(
                                employees.find(e => e.id === (selected.pointOfContactId ?? null))?.displayName || ''
                            );
                            setErrors({});
                        }}
                        inputValue={formData.name}
                        onInputChange={(_, value, reason) => {
                            if (reason === 'input') {
                                setIsExistingActivity(false);
                                handleChange('name', sanitizeNameInput(value));
                            } 
                            else if (reason === 'clear' || value === '') {

    if (isExistingActivity && isAddMode) {

        // Clear autofilled data only for existing-activity selection in add mode
        setIsExistingActivity(false);

        setFormData({
            id: 0,
            categoryId: 0,
            technologyId: 0,
            name: '',
            description: '',
            pointOfContactId: null,
            status: '',
        });

        setCategoryInput('');
        setTechnologyInput('');
        setPointOfContactInput('');

    } else {

        // Only clear activity name
        setIsExistingActivity(false);

        handleChange('name', '');
        handleChange('id', 0);
    }
}
                        }}
                        size="small"
                        slotProps={{
                            paper: { sx: dropdownMenuSx },
                            popper: { sx: { zIndex: 1302 } },
                        }}
                        renderInput={(params) => (
                            <>
                                <TextField
                                    {...params}
                                    placeholder="Activity Name"
                                    error={Boolean(errors.name) && (!snackbar.open || errors.name !== 'Activity name already exists')}
                                    helperText={
                                        snackbar.open && errors.name === 'Activity name already exists'
                                            ? ''
                                            : errors.name && isDark
                                                ? <span style={errorTextStyle}>{errors.name}</span>
                                                : errors.name
                                    }
                                    FormHelperTextProps={{
    style: {
        ...errorTextStyle,
        boxShadow: 'none',
        outline: 'none',
        border: 'none',
    }
}}
                                    inputProps={{ ...params.inputProps, maxLength: 50 }}
                                    sx={inputStyle}
                                />
                                <Typography variant="caption" className="character-count" sx={{ fontSize: '11px', mt: 0.5, ml: 0 }}>
                                    {formData.name.length}/50 characters
                                </Typography>
                            </>
                        )}
                    />
                </div>

                {/* 2. Category */}
                <div style={fieldWrap}>
                    <label style={labelStyle}>
                        Category<span style={requiredStar}>*</span>
                    </label>
                    <Autocomplete
                        
                        popupIcon={<ArrowDropDownIcon />}
                        forcePopupIcon={true}
                        options={categories
                            .filter((c: any) => String(c.status || '').toLowerCase() === 'active')
                            .slice()
                            .sort((a: any, b: any) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()))}
                        value={categories.find(c => c.id === formData.categoryId) ?? null}
                        inputValue={categoryInput}
                        onInputChange={(_, newInput) => setCategoryInput(newInput)}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option?.name ?? ''}
                        isOptionEqualToValue={(option, value) => option.id === (value as any)?.id}
                        onChange={(_, value) => {
                            if (isExistingActivity && isAddMode) return;
                            if (typeof value === 'string') {
                                setCategoryInput(value);
                                handleChange('categoryId', 0);
                            } else if (value) {
                                setCategoryInput(value.name);
                                handleChange('categoryId', value.id);
                            } else {
                                setCategoryInput('');
                                handleChange('categoryId', 0);
                            }
                        }}
                        disabled={isExistingActivity && isAddMode}
                        
                        sx={{
                            ...inputStyle,
                            ...(isDark && isExistingActivity && isAddMode && {
                                '& .MuiInputBase-input.Mui-disabled': {
                                    color: '#94a3b8',
                                    WebkitTextFillColor: '#94a3b8',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255,255,255,0.15)',
                                },
                            }),
                        }}
                        clearIcon={null}
                        size="small"
                        slotProps={{
                            paper: { sx: dropdownMenuSx },
                            popper: { sx: { zIndex: 1302 } },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select"
                                error={Boolean(errors.categoryId)}
                                helperText={
                                    errors.categoryId && isDark
                                        ? <span style={errorTextStyle}>{errors.categoryId}</span>
                                        : errors.categoryId
                                }
                                FormHelperTextProps={{
    style: {
        ...errorTextStyle,
        boxShadow: 'none',
        outline: 'none',
        border: 'none',
    }
}}
                                InputProps={{
    ...params.InputProps,
    endAdornment: (
        <>
            {categoryInput && !isExistingActivity && (
                <IconButton
                    size="small"
                    onClick={() => {
                        setCategoryInput('');
                        handleChange('categoryId', 0);
                    }}
                    edge="end"
                    sx={{
                        color: '#6b7280',
                        padding: 0,
                        position: 'absolute',
                        right: '38px',
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            )}

            {params.InputProps.endAdornment}
        </>
    ),
}}
                                sx={inputStyle}
                            />
                        )}
                    />
                </div>

                {/* 3. Technology */}
                <div style={fieldWrap}>
                    <label style={labelStyle}>
                        Technology<span style={requiredStar}>*</span>
                    </label>
                    <Autocomplete
                        
                        popupIcon={<ArrowDropDownIcon />}
                        forcePopupIcon={true}
                        options={technologies
                            .filter((t: any) => String(t.status || '').toLowerCase() === 'active')
                            .slice()
                            .sort((a: any, b: any) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()))}
                        value={formData.technologyId ? technologies.find(t => t.id === formData.technologyId) ?? null : null}
                        inputValue={technologyInput}
                        onInputChange={(_, newInput) => setTechnologyInput(newInput)}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option?.name ?? ''}
                        isOptionEqualToValue={(option, value) => option.id === (value as any)?.id}
                        onChange={(_, value) => {
                            if (isExistingActivity && isAddMode) return;
                            if (typeof value === 'string') {
                                setTechnologyInput(value);
                                handleChange('technologyId', 0);
                            } else if (value) {
                                setTechnologyInput(value.name);
                                handleChange('technologyId', value.id);
                            } else {
                                setTechnologyInput('');
                                handleChange('technologyId', 0);
                            }
                        }}
                        disabled={isExistingActivity && isAddMode}
                        sx={{
                            ...inputStyle,
                            ...(isDark && isExistingActivity && isAddMode && {
                                '& .MuiInputBase-input.Mui-disabled': {
                                    color: '#94a3b8',
                                    WebkitTextFillColor: '#94a3b8',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255,255,255,0.15)',
                                },
                            }),
                        }}
                        clearIcon={null}
                        size="small"
                        slotProps={{
                            paper: { sx: dropdownMenuSx },
                            popper: { sx: { zIndex: 1302 } },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select"
                                error={Boolean(errors.technologyId)}
                                helperText={
                                    errors.technologyId && isDark
                                        ? <span style={errorTextStyle}>{errors.technologyId}</span>
                                        : errors.technologyId
                                }
                                FormHelperTextProps={{
    style: {
        ...errorTextStyle,
        boxShadow: 'none',
        outline: 'none',
        border: 'none',
    }
}}
                                InputProps={{
    ...params.InputProps,
    endAdornment: (
        <>
            {technologyInput && !isExistingActivity && (
                <IconButton
                    size="small"
                    onClick={() => {
                        setTechnologyInput('');
                        handleChange('technologyId', 0);
                    }}
                    edge="end"
                    sx={{
                        color: '#6b7280',
                        padding: 0,
                        position: 'absolute',
                        right: '38px',
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            )}

            {params.InputProps.endAdornment}
        </>
    ),
}}
                                sx={inputStyle}
                            />
                        )}
                    />
                </div>

                {/* 4. Description */}
                <div style={fieldWrap}>
                    <label style={labelStyle}>
                        Description<span style={requiredStar}>*</span>
                    </label>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Write here..."
                        value={formData.description}
                        onChange={e => {
                            if (e.target.value.length <= 300) {
                                handleChange('description', e.target.value);
                            }
                        }}
                        error={Boolean(errors.description)}
                        helperText={
                            errors.description
                                ? (isDark ? <span style={errorTextStyle}>{errors.description}</span> : errors.description)
                                : <span className="character-count">{`${formData.description.length}/300 characters`}</span>
                        }
                        FormHelperTextProps={{
    style: {
        ...errorTextStyle,
        boxShadow: 'none',
        outline: 'none',
        border: 'none',
    }
}}
                        inputProps={{ maxLength: 300 }}
                        disabled={isExistingActivity && isAddMode}
                        size="small"
                        sx={{
                            ...inputStyle,
                            ...(isDark && isExistingActivity && isAddMode && {
                                '& .MuiInputBase-input.Mui-disabled': {
                                    color: '#94a3b8',
                                    WebkitTextFillColor: '#94a3b8',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255,255,255,0.15)',
                                },
                            }),
                        }}
                        InputProps={{
                            endAdornment: (
                                formData.description && !(isExistingActivity && isAddMode)? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            aria-label="clear description"
                                            onClick={() => {
                                                handleChange('description', '');
                                            }}
                                            edge="end"
                                            sx={{ color: '#6b7280', padding: 0, marginRight: '2px', '&:hover': { background: 'rgba(0,0,0,0.04)', color: '#6b7280' } }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null
                            ),
                        }}
                    />
                </div>

                {/* 5. Point of Contact */}
                <div style={fieldWrap}>
                    <label style={labelStyle}>
                        Point of Contact<span style={requiredStar}>*</span>
                    </label>
                    <Autocomplete
                        
                        popupIcon={<ArrowDropDownIcon />}
                        forcePopupIcon={true}
                        options={employees.slice().sort((a: any, b: any) => {
                            const na = (a.displayName || a.name || a.empName || '').toString().toLowerCase();
                            const nb = (b.displayName || b.name || b.empName || '').toString().toLowerCase();
                            return na.localeCompare(nb);
                        })}
                        value={
                            formData.pointOfContactId
                                ? employees.find(e => e.id === formData.pointOfContactId) ?? null
                                : null
                        }
                        inputValue={pointOfContactInput}
                        onInputChange={(_, newInput) => setPointOfContactInput(newInput)}
                        getOptionLabel={option => typeof option === 'string' ? option : option?.displayName ?? ''}
                        isOptionEqualToValue={(option, value) => option.id === (value as any)?.id}
                        onChange={(_, value) => {
                            if (isExistingActivity && isAddMode) return;
                            if (typeof value === 'string') {
                                setPointOfContactInput(value);
                                handleChange('pointOfContactId', null);
                            } else if (value) {
                                setPointOfContactInput(value.displayName);
                                handleChange('pointOfContactId', value.id);
                            } else {
                                setPointOfContactInput('');
                                handleChange('pointOfContactId', null);
                            }
                        }}
                        disabled={isExistingActivity && isAddMode}
                        sx={{
                            ...inputStyle,
                            ...(isDark && isExistingActivity && isAddMode && {
                                '& .MuiInputBase-input.Mui-disabled': {
                                    color: '#94a3b8',
                                    WebkitTextFillColor: '#94a3b8',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255,255,255,0.15)',
                                },
                            }),
                        }}
                        clearIcon={null}
                        size="small"
                        slotProps={{
                            paper: { sx: dropdownMenuSx },
                            popper: { sx: { zIndex: 1302 } },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select"
                                error={Boolean(errors.pointOfContactId)}
                                helperText={
                                    errors.pointOfContactId && isDark
                                        ? <span style={errorTextStyle}>{errors.pointOfContactId}</span>
                                        : errors.pointOfContactId
                                }
                                FormHelperTextProps={{
    style: {
        ...errorTextStyle,
        boxShadow: 'none',
        outline: 'none',
        border: 'none',
    }
}}
                                InputProps={{
    ...params.InputProps,
    endAdornment: (
        <>
            {pointOfContactInput && !isExistingActivity && (
                <IconButton
                    size="small"
                    onClick={() => {
                        setPointOfContactInput('');
                        handleChange('pointOfContactInput', 0);
                    }}
                    edge="end"
                    sx={{
                        color: '#6b7280',
                        padding: 0,
                        position: 'absolute',
                        right: '38px',
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            )}

            {params.InputProps.endAdornment}
        </>
    ),
}}
                                sx={inputStyle}
                            />
                        )}
                    />
                </div>

                {/* 6. Status */}
                <div style={{ ...fieldWrap, marginBottom: 0 }}>
                    <label style={labelStyle}>
                        Status<span style={requiredStar}>*</span>
                    </label>
                    <Select
                        fullWidth
                        displayEmpty
                        value={formData.status}
                        onChange={e => handleChange('status', e.target.value)}
                        disabled={isExistingActivity && isAddMode}
                        sx={{
                            ...statusStyle,
                            ...(isDark && isExistingActivity && isAddMode && {
                                '& .MuiInputBase-input.Mui-disabled': {
                                    color: '#94a3b8',
                                    WebkitTextFillColor: '#94a3b8',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255,255,255,0.15)',
                                },
                            }),
                        }}
                        size="small"
                        error={Boolean(errors.status)}
                        renderValue={(val) => val || <span style={{ color: '#9e9e9e', fontSize: '12px' }}>Select</span>}
                        MenuProps={{
                            PaperProps: {
                                sx: dropdownMenuSx,
                            },
                        }}
                    >
                        <MenuItem value="Active" sx={{ fontSize: '12px', ...dropdownMenuSx }}>Active</MenuItem>
                        <MenuItem value="Inactive" sx={{ fontSize: '12px', ...dropdownMenuSx }}>Inactive</MenuItem>
                    </Select>
                    {errors.status && (
                        <div style={{ color: isDark ? '#d32f2f' : '#d32f2f', fontSize: '0.75rem', marginTop: '4px' }}>{errors.status}</div>
                    )}
                </div>

            </DialogContent>

            {/* ── Footer Buttons ── */}
            <DialogActions sx={{ px: 3, pb: 2, pt: 1.5, gap: 1.5, justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    sx={{
                        backgroundColor: isDark ? '#38bdf8' : '#124B84',
                        color: '#fff',
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '13px',
                        px: 3,
                        '&:hover': { backgroundColor: isDark ? '#0ea5e9' : '#0f3f70' },
                        '&:disabled': { backgroundColor: '#bdbdbd' },
                    }}
                >
                    {activity ? 'Update' : 'Create'}
                </Button>
                <Button
                    variant="contained"
                    onClick={onClose}
                    sx={{
                        backgroundColor: isDark ? '#e53935' : '#e53935',
                        color: '#fff',
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '13px',
                        px: 3,
                        '&:hover': { backgroundColor: isDark ? '#c62828' : '#c62828' },
                    }}
                >
                    Cancel
                </Button>
            </DialogActions>
        {/* Snackbar for error at bottom of modal */}
        {snackbar.open && (
            <div
                style={{
                    position: 'fixed',
                    bottom: 20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#d32f2f',
                    color: '#fff',
                    padding: '10px 24px',
                    borderRadius: 8,
                    zIndex: 9999,
                    fontWeight: 500,
                    fontSize: 15,
                    boxShadow: '0 2px 8px 0 #0003',
                    cursor: 'pointer',
                }}
                onClick={() => {
                    setSnackbar({ open: false, message: '' });
                    if (snackbarTimer.current) clearTimeout(snackbarTimer.current);
                }}
            >
                {snackbar.message}
            </div>
        )}
        </Dialog>
    );
};

export default ActivityModal;